import { max, once } from 'lodash'
import moment from 'moment'

import { AnniversaryCardInfoFragment } from '@/generated/graphql'

export const getBackgroundColor = (anniversaryYears: number) => {
  switch (anniversaryYears) {
    case 1:
      return '#caf0ff'
    case 2:
      return '#ebeedf'
    case 3:
      return '#cdcdf3'
    case 4:
      return '#f1e8dd'
    case 5:
      return '#fddade'
    case 6:
      return '#fdeae3'
    case 7:
      return '#e7f1ce'
    case 8:
      return '#e6e6f2'
    case 9:
      return '#d9e9fb'
    case 10:
      return '#fadeea'
    default:
      return '#103e92'
  }
}

export const getFillStyle = (anniversaryYears: number) => {
  switch (anniversaryYears) {
    case 1:
      return '#2e6590'
    case 2:
      return '#5c3b74'
    case 3:
      return '#5b59aa'
    case 4:
      return '#534544'
    case 5:
      return '#8c5581'
    case 6:
      return '#4b2c31'
    case 7:
      return '#717543'
    case 8:
      return '#292479'
    case 9:
      return '#492078'
    case 10:
      return '#4e416d'
    default:
      return 'black'
  }
}

export const drawAnniversaryCard = (
  ctx: CanvasRenderingContext2D,
  image: HTMLImageElement,
  anniversaryCard: AnniversaryCardInfoFragment,
  anniversaryYears: number
) => {
  const width = 640
  const paddingWidth = 74
  const paddingHeight = 10
  const lineHeight = 30
  let currentHeight = 500
  ctx!.drawImage(image, 0, 0)
  ctx!.fillStyle = getFillStyle(anniversaryYears)
  ctx!.font = `20px ACFont`
  currentHeight = drawHeader({
    ctx,
    content: anniversaryCard.header || `Hi ${anniversaryCard!.cardReceiver.name}：`,
    x: paddingWidth,
    y: currentHeight,
    lineHeight,
  })
  const paragraphs = anniversaryCard!.content.split('\n')

  paragraphs.forEach(
    (paragraph, index) =>
      (currentHeight = drawContent({
        ctx,
        content: paragraph,
        x: paddingWidth,
        y: currentHeight + paddingHeight * (index === 0 ? 2 : 1),
        width: width - paddingWidth * 2,
        lineHeight,
      }))
  )

  const creatorName = anniversaryCard.footer || `From ${anniversaryCard!.cardCreator.name}`
  const department = anniversaryCard.footer ? '' : anniversaryCard!.cardCreator.departments[0]?.name.trim() || ''
  const createdDate = moment(anniversaryCard!.hireDate).year(Number(anniversaryCard!.anniversary)).format('LL')
  const footerWidth = max([creatorName, department, createdDate].map((s) => ctx.measureText(s).width))
  const footerX = width - paddingWidth - footerWidth! / 2

  if (creatorName) {
    currentHeight = drawFooter({
      ctx,
      content: creatorName,
      x: footerX,
      y: currentHeight + paddingHeight * 2,
      lineHeight,
      textAlign: 'center',
    })
  }

  // if (department) {
  //   currentHeight = drawFooter({
  //     ctx,
  //     content: department,
  //     x: footerX,
  //     y: currentHeight + paddingHeight,
  //     lineHeight,
  //     textAlign: 'center',
  //   })
  // }

  if (createdDate) {
    drawFooter({
      ctx,
      content: createdDate,
      x: footerX,
      y: currentHeight + paddingHeight,
      lineHeight,
      textAlign: 'center',
    })
  }
}

const drawHeader = ({
  ctx,
  content,
  x,
  y,
  lineHeight,
}: {
  ctx: CanvasRenderingContext2D
  content: string
  x: number
  y: number
  lineHeight: number
}) => {
  ctx!.fillText(content, x, y)
  return y + lineHeight
}

const drawContent = ({
  ctx,
  content,
  x,
  y,
  width,
  lineHeight,
}: {
  ctx: CanvasRenderingContext2D
  content: string
  x: number
  y: number
  width: number
  lineHeight: number
}) => {
  let offset = 0
  let axisY = y
  const end = content.length
  while (offset < end) {
    // 第一行内容固定为 Hi xxx:
    // content 首行空两个字符
    const firstLineIntent = ctx.measureText('Hi').width
    const axisX = offset === 0 ? x + firstLineIntent : x
    const limit = findCharacterCountPerLine(ctx, content, offset === 0 ? width - firstLineIntent : width, offset)
    ctx.fillText(content.slice(offset, offset + limit), axisX, axisY)
    axisY += lineHeight
    offset += limit
  }

  return axisY
}

const drawFooter = ({
  ctx,
  content,
  x,
  y,
  lineHeight,
  textAlign = 'center',
}: {
  ctx: CanvasRenderingContext2D
  content: string
  x: number
  y: number
  lineHeight: number
  textAlign?: 'left' | 'right' | 'center' | 'start' | 'end'
}) => {
  ctx.textAlign = textAlign
  ctx.fillText(content, x, y)
  // reset 到 默认值
  ctx.textAlign = 'start'
  return y + lineHeight
}

export const findCharacterCountPerLine = (
  ctx: CanvasRenderingContext2D,
  content: string,
  width: number,
  offset: number
) => {
  let limit = 25
  while (
    width < ctx.measureText(content.slice(offset, offset + limit)).width ||
    width > ctx.measureText(content.slice(offset, offset + limit + 1)).width
  ) {
    if (width < ctx.measureText(content.slice(offset, offset + limit)).width) {
      limit -= 1
    }

    if (width > ctx.measureText(content.slice(offset, offset + limit + 1)).width) {
      if (offset + limit >= content.length) {
        break
      }
      limit += 1
    }
  }
  return limit
}

export const getAnniversaryBackgroundImageUrl = (anniversary: number) =>
  `https://agora-web-cdn.oss-cn-beijing.aliyuncs.com/tale/backgrounds/anniversary_card_${anniversary}.jpg` +
  '?_=' +
  Math.round(Math.random() * 1000000)

export const loadFont = once((fontName: string) => {
  // 初始化字体
  const fontFace = new FontFace(
    fontName,
    'url("https://agora-web-cdn.oss-cn-beijing.aliyuncs.com/tale/Alibaba-PuHuiTi-Medium.ttf")'
  )
  document.fonts['add'](fontFace)

  fontFace.load()
  return fontFace
})
